import { TikTokToken } from '@remotion/captions';
import { FontStyle, WORD_BREAK_STYLE } from '../text/text-item-type';
import { resolveFontFamilyCssStack } from '../../utils/text/font-family-css-stack';
import { turnFontStyleIntoCss } from '../../utils/text/turn-font-style-into-css';
import { getCaptionTokenDisplayText } from '@/shared/lib/captions/caption-token-display-text';

/**
 * 토큰 span 구조를 실제 DOM에 렌더링하여 maxLines에 맞는 최대 fontSize를 계산한다.
 *
 * Remotion의 fitTextOnNLines는 단일 텍스트로 측정하지만,
 * 실제 캡션은 토큰별 <span>으로 렌더링되어 줄바꿈 동작이 다르다.
 * 이 함수는 실제 렌더링 구조와 동일한 토큰 span으로 측정하여 불일치를 해결한다.
 */
export const fitTokensOnNLines = ({
  tokens,
  maxBoxWidth,
  maxLines,
  maxFontSize,
  fontFamily,
  fontStyle,
  letterSpacing,
  lineHeight,
}: {
  tokens: TikTokToken[];
  maxBoxWidth: number;
  maxLines: number;
  maxFontSize: number;
  fontFamily: string;
  fontStyle: FontStyle;
  letterSpacing: number;
  lineHeight: number;
}): { fontSize: number } => {
  if (typeof document === 'undefined' || tokens.length === 0) {
    return { fontSize: maxFontSize };
  }

  const fontCss = turnFontStyleIntoCss(fontStyle);
  const cssFontFamily = resolveFontFamilyCssStack(fontFamily);

  // 측정용 컨테이너 생성 (실제 캡션 렌더링 구조 재현)
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'absolute',
    left: '-9999px',
    top: '-9999px',
    visibility: 'hidden',
    width: `${maxBoxWidth}px`,
    fontFamily: cssFontFamily,
    fontWeight: fontCss.fontWeight ?? 'normal',
    fontStyle: fontCss.fontStyle ?? 'normal',
    lineHeight: String(lineHeight),
    letterSpacing: `${letterSpacing}px`,
    ...WORD_BREAK_STYLE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const wrapper = document.createElement('span');
  Object.assign(wrapper.style, {
    position: 'relative',
    display: 'inline-block',
    whiteSpace: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    fontFamily: 'inherit',
  });

  const textSpan = document.createElement('span');
  Object.assign(textSpan.style, {
    position: 'relative',
    display: 'block',
    whiteSpace: 'inherit',
    fontFamily: 'inherit',
  });

  for (const [index, token] of tokens.entries()) {
    const s = document.createElement('span');
    Object.assign(s.style, {
      display: 'inline',
      whiteSpace: 'pre-wrap',
      fontFamily: 'inherit',
    });
    // 측정 시 \n을 공백으로 치환하여 강제 줄바꿈을 제거한다.
    // \n은 사용자가 Shift+Enter로 추가한 것이지만, 폰트 크기 계산에서는
    // CSS 자동 줄바꿈(text-wrap: balance)에 맡기고 \n으로 인한 이중 줄바꿈을 방지한다.
    s.textContent = getCaptionTokenDisplayText(tokens, index).replace(
      /\n/g,
      ' ',
    );
    textSpan.appendChild(s);
  }

  wrapper.appendChild(textSpan);
  container.appendChild(wrapper);
  document.body.appendChild(container);

  // binary search: maxLines에 맞는 최대 fontSize 찾기
  let lo = 1;
  let hi = maxFontSize;
  let bestSize = 1;

  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    container.style.fontSize = `${mid}px`;

    const lineHeightPx = mid * lineHeight;
    const measuredLines = Math.max(
      1,
      Math.round(textSpan.offsetHeight / lineHeightPx),
    );

    if (measuredLines <= maxLines) {
      bestSize = mid;
      lo = mid;
    } else {
      hi = mid;
    }

    // 충분히 수렴하면 종료
    if (hi - lo < 0.5) break;
  }

  document.body.removeChild(container);

  return { fontSize: Math.floor(bestSize) };
};
